import { cx } from "@suse-o11y/class-variance";
import { copyToClipboard } from "@suse-o11y/copy-to-clipboard";
import { IconCopyOutlined } from "@suse-o11y/icons";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { Editor } from "~/components/code/editor";

import { useExportNodeQuery } from "../../queries";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { DONT_AUTO_DISMISS, notification } from "../react/notification/Notification";

interface DefinitionTabContentProps {
	className?: string;
	monitorId: number;
	title: string;
}

export const DefinitionTabContent: React.FC<DefinitionTabContentProps> = ({ className, monitorId, title }) => {
	const { data: yaml = "", isLoading, error, refetch } = useExportNodeQuery(monitorId);

	if (isLoading) {
		return <div className={cx("skeleton", className)} />;
	}

	if (error != null) {
		return (
			<GenericErrorMessage
				className="h-full"
				error={error}
				highlightText="Failed"
				onRetry={() => refetch()}
				text={`Failed to load monitor definition for '${title}`}
			/>
		);
	}

	return (
		<div className={cx("group relative", className)}>
			<TooltipBoundary disableHoverableContent>
				<TooltipTrigger
					aria-label="Copy to clipboard"
					className="button button-circle button-lg button-tertiary absolute top-2 right-2 z-1 opacity-0 transition-opacity group-hover:opacity-100"
					onClick={async () => {
						const [failure, success] = await copyToClipboard({
							text: yaml,
							label: "monitor definition",
						});

						if (failure == null) {
							notification.success({ message: success });
						} else {
							notification.error({
								message: failure,
								duration: DONT_AUTO_DISMISS,
							});
						}
					}}
				>
					<IconCopyOutlined />
				</TooltipTrigger>

				<Tooltip className="tooltip/tooltip">Copy to clipboard</Tooltip>
			</TooltipBoundary>

			<Editor height="100%" language="yaml" readOnly value={yaml} />
		</div>
	);
};
